// src/central/Layout.js
import React, { useState, useRef, useEffect } from "react";
import TopBar from "./TopBar";
import LeagueNav from "./LeagueNav";
import { useLocation } from "react-router-dom";
import { useGuardedNavigate } from "../utils/navGuard";
import { useAuth } from "../context/AuthContext";
import { doc, getDoc, onSnapshot, updateDoc } from "firebase/firestore";
import { db } from "../lib/firebase";
import styled from "styled-components";
import LoadingOverlay from "./LoadingOverlay";
import { LoadingProvider, useLoading } from "./LoadingContext";
import { buildFeedbackUrl } from "../utils/feedback";
import BottomNav from "../components/BottomNav";
import UpdateBanner from "../components/UpdateBanner";

// Navbar structure
const Navbar = styled.header`
  display: flex;
  align-items: center;
  padding: 0.4rem 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.textOnPrimary};
`;
// Sticky container for Navbar + TopBar + LeagueNav
const StickyHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  width: 100%;
  background: ${({ theme }) => theme.colors.backgound || "#F5F5F5"};
  box-shadow: 0 2px 6px rgba(0,0,0,0.04);
`;

const Logo = styled.span`
  font-weight: 700;
  font-size: 1.4rem;
  letter-spacing: 0.01em;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
`;

const NavSection = styled.div`
  flex: ${({ flex }) => flex || 1};
  display: flex;
  justify-content: ${({ justify }) => justify || "flex-start"};
  align-items: center;
`;

const NavButton = styled.button`
  background: none;
  border: none;
  font: inherit;
  cursor: pointer;
  padding: ${({ theme }) => `${theme.spacing.small} ${theme.spacing.medium}`};
  border-radius: ${({ theme }) => theme.radii.small};
  color: ${({ theme }) => theme.colors.textOnPrimary};
  font-weight: ${({ bold }) => (bold ? 600 : 500)};
  font-size: ${({ size }) => (size === "large" ? "1.1rem" : "1rem")};
  transition: background-color ${({ theme }) => theme.transitions.fast};
  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryHover};
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  left: ${({ alignRight }) => (alignRight ? "auto" : 0)};
  right: ${({ alignRight }) => (alignRight ? 0 : "auto")};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadows.subtle};
  z-index: 1000;
  pointer-events: auto;
  min-width: 160px;
  padding: 0.25rem 0;
  border-radius: ${({ theme }) => theme.radii.small};
`;

const DropdownItem = styled.div`
  padding: ${({ theme }) => `${theme.spacing.small} ${theme.spacing.medium}`};
  cursor: pointer;
  background: ${({ active, theme }) => (active ? theme.colors.backgroundAlt : theme.colors.surface)};
  font-weight: ${({ active }) => (active ? "bold" : "normal")};
  color: ${({ active, theme }) => (active ? theme.colors.primary : theme.colors.text)};
  &:hover {
    background: ${({ theme }) => theme.colors.backgroundAlt};
  }
`;

const LogoutDropdownItem = styled(DropdownItem)`
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  margin-top: 0.25rem;
  color: ${({ theme }) => theme.colors.error};
`;

const LayoutInner = ({ children }) => {
  const { loading } = useLoading();
  const { logout, currentUser } = useAuth();
  const [userData, setUserData] = useState(null);
  const navigate = useGuardedNavigate();
  const location = useLocation();

  const handleOpenFeedback = (source) => {
    const leagueID = currentLeague?.id || userData?.currentLeagueID || "";
    const userID = currentUser?.uid || "";
    const role = leagueID && userData?.roles ? userData.roles[leagueID] || "" : "";

    const url = buildFeedbackUrl({
      leagueID,
      userID,
      source,
      role,
    });

    window.open(url, "_blank", "noopener,noreferrer");
  };

  // Dropdowns: leagues, user
  const [leagueDropdownOpen, setLeagueDropdownOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);

  const leagueDropdownRef = useRef(null);
  const userDropdownRef = useRef(null);

  useEffect(() => {
    if (!currentUser?.uid) return;
    const userRef = doc(db, "users", currentUser.uid);
    const unsub = onSnapshot(userRef, (snap) => {
      if (snap.exists()) setUserData(snap.data());
    });
    return () => unsub();
  }, [currentUser]);

  const [leagueNames, setLeagueNames] = useState({});

  // Normalize leagues data from Firestore structure
  const leagueIDs = userData?.leagueIDs || [];
  const userLeagues = leagueIDs.map((id) => ({
    id,
    teamID: userData?.teams?.[id] || null,
    role: userData?.roles?.[id] || null,
    name: leagueNames[id] || id,
  }));

  useEffect(() => {
    async function fetchLeagueNames() {
      const names = {};
      for (const league of userLeagues) {
        try {
          const docRef = doc(db, "leagues", league.id);
          const docSnap = await getDoc(docRef);
          names[league.id] = docSnap.exists() ? docSnap.data().name || league.id : league.id;
        } catch {
          names[league.id] = league.id;
        }
      }
      setLeagueNames(names);
    }
    if (userLeagues.length > 0) fetchLeagueNames();
  }, [userLeagues]);

  useEffect(() => {
    if (!currentUser?.uid) return;
    if (!userData) return;
    if (!userData.currentLeagueID && userLeagues && userLeagues.length > 0) {
      const firstLeagueID = userLeagues[0].id;
      const userRef = doc(db, "users", currentUser.uid);
      updateDoc(userRef, { currentLeagueID: firstLeagueID }).catch(() => {});
    }
  }, [currentUser, userData, userLeagues]);

  // Current league
  let currentLeague = null;
  if (userData && userLeagues.length > 0) {
    const activeLeagueID =
      userData.currentLeagueID || userLeagues[0].id;

    currentLeague = userLeagues.find(
      (league) => league.id === activeLeagueID
    );
  }

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClick = (e) => {
      if (
        (leagueDropdownRef.current && !leagueDropdownRef.current.contains(e.target)) ||
        (userDropdownRef.current && !userDropdownRef.current.contains(e.target))
      ) {
        setLeagueDropdownOpen(false);
        setUserDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const handleLeagueSelect = async (leagueID) => {
    try {
      setLeagueDropdownOpen(false);
      if (currentUser?.uid) {
        const userRef = doc(db, "users", currentUser.uid);
        await updateDoc(userRef, { currentLeagueID: leagueID });
      }
      navigate(`/pickteam/${leagueID}`);
    } catch (error) {
      console.error("Failed to set currentLeagueID:", error);
      navigate(`/pickteam/${leagueID}`);
    }
  };

  // League dropdown logic

  const profileIncomplete = userData && (
    !userData.linkedPlayerIDs?.[userData.currentLeagueID] ||
    userData.notificationPermission !== "granted"
  );

  // User dropdown
  const userDropdown = (
    <div style={{ position: "relative", display: "inline-block", marginLeft: "0rem" }} ref={userDropdownRef}>
      <NavButton size="large" onClick={() => setUserDropdownOpen(!userDropdownOpen)} aria-label="User menu">
      ☰
      </NavButton>
      {userDropdownOpen && (
        <Dropdown alignRight>
          <DropdownItem
            onClick={() => {
              setUserDropdownOpen(false);
              navigate("/home");
            }}
            onMouseDown={(e) => e.preventDefault()}
          >
            My Leagues
          </DropdownItem>
          <DropdownItem
            onClick={() => {
              setUserDropdownOpen(false);
              navigate("/profile");
            }}
            onMouseDown={(e) => e.preventDefault()}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              Profile
              {profileIncomplete && (
                <span style={{
                  width: 8, height: 8, borderRadius: "50%",
                  background: "#E53935", display: "inline-block", flexShrink: 0
                }} />
              )}
            </span>
          </DropdownItem>
          <DropdownItem
            onClick={() => {
              setUserDropdownOpen(false);
              navigate("/join");
            }}
            onMouseDown={(e) => e.preventDefault()}
          >
            Join League
          </DropdownItem>
          <DropdownItem
            onClick={() => {
              setUserDropdownOpen(false);
              navigate("/create");
            }}
            onMouseDown={(e) => e.preventDefault()}
          >
            Create League
          </DropdownItem>
          <DropdownItem
            onClick={() => {
              setUserDropdownOpen(false);
              navigate("/faq");
            }}
            onMouseDown={(e) => e.preventDefault()}
          >
            FAQ
          </DropdownItem>
          <DropdownItem
            onClick={() => {
              setUserDropdownOpen(false);
              navigate("/help");
            }}
            onMouseDown={(e) => e.preventDefault()}
          >
            Help & Contact
          </DropdownItem>
          <DropdownItem
            onClick={() => {
              setUserDropdownOpen(false);
              navigate("/status");
            }}
            onMouseDown={(e) => e.preventDefault()}
          >
            App Status
          </DropdownItem>
          <DropdownItem
            onClick={() => {
              setUserDropdownOpen(false);
              handleOpenFeedback("profile_dropdown_feedback");
            }}
            onMouseDown={(e) => e.preventDefault()}
          >
            Feedback (30 Sec)
          </DropdownItem>
          <LogoutDropdownItem
            onMouseDown={(e) => {
              e.stopPropagation();
              setUserDropdownOpen(false);
              handleLogout();
            }}
          >
            Sign Out
          </LogoutDropdownItem>
        </Dropdown>
      )}
    </div>
  );

  return (
    <>
      <LoadingOverlay show={loading} />

      <div>
          <StickyHeader>
            <Navbar>
              <NavSection flex={1}>
                <Logo onClick={() => navigate("/")}> 
                  ClubWeek
                </Logo>
              </NavSection>

              <NavSection flex={2} justify="center"></NavSection>

              <NavSection flex={1} justify="flex-end">
                {currentUser && userDropdown}
              </NavSection>
            </Navbar>

            {currentUser && userData && (
              <>
                <TopBar currentLeagueID={userData.currentLeagueID} userData={userData} />
                <LeagueNav />
              </>
            )}
          </StickyHeader>

          <main style={{ padding: "0.5rem 2rem" }}
            className="layout-main"
          >
            {children}
          </main>
          <BottomNav />
        </div>
        <style>{`
          @media (max-width: 767px) {
            .layout-main { padding-bottom: calc(60px + env(safe-area-inset-bottom)) !important; }
          }
        `}</style>
        <UpdateBanner />
    </>
  );
};

const Layout = ({ children }) => {
  return (
    <LoadingProvider>
      <LayoutInner>{children}</LayoutInner>
    </LoadingProvider>
  );
};

export default Layout;